import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { SequenceStatusEnum, SequenceType } from "../../../types/Questionnaire";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/ConfigContext";
import { getApiSequences } from "../../../services/questionnaire.service";
import { getUserScores } from "../../../services/sequence.service";

// --- Types ---
interface Score {
  id: number; //this is also the user sequence component id
  userSequenceId: number;
  sequenceId: number;
  sequenceComponentId: number;
  questionnaireId: number;
  score: number;
  doneAt: string;
}

interface ResonanceDataContextValue {
  sequences: SequenceType[];
  scores: Score[];
  loading: boolean;
  error: Error | null;

  fetchSequences: () => Promise<void>;
  fetchScores: () => Promise<void>;
  updateScore: (
    id: number,
    userSequenceId: number,
    sequenceId: number,
    sequenceComponentId: number,
    questionnaireId: number,
    score: number,
  ) => void;

  updateSequenceComponentDone: (
    userSequenceId: number,
    sequenceComponentId: number,
  ) => void;

  updateSequenceComponentProgress: (
    userSequenceId: number,
    sequenceComponentId: number,
    answeredCount: number
  ) => void;

  updateSequenceDone: (userSequenceId: number) => void;
}

// --- Default value ---
const ResonanceDataContext = createContext<ResonanceDataContextValue>({
  sequences: [],
  scores: [],
  loading: false,
  error: null,
  fetchSequences: async () => {},
  fetchScores: async () => {},
  updateScore: () => {},
  updateSequenceComponentDone: () => {},
  updateSequenceDone: () => {},
  updateSequenceComponentProgress: () => {} 
});

// --- Provider ---
export const ResonanceDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { study } = useConfig();
  const {
    i18n: { language },
  } = useTranslation();

  const [sequences, setSequences] = useState<SequenceType[]>([]);
  const [scores, setScores] = useState<Score[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // fetch sequences
  const fetchSequences = async () => {
    if (!user || !study?.id) return;
    setLoading(true);
    setError(null);

    try {
      const response = await getApiSequences({
        studyId: study.id,
        userId: user.id,
        lang: language,
      });
      setSequences(response.data);
    } catch (err: any) {
      console.error("Failed to fetch sequences", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

 interface ScoresResponse {
  scores: Score[];
  success?: boolean;
}

const fetchScores = async () => {
  if (!user || !study?.id) return;
  try {
    console.log("Fetching scores with", { studyId: study.id, userId: user.id });
    const response = await getUserScores({
      studyId: study.id,
      userId: user.id,
    });

    console.log("getUserScores raw response:", response);

    const raw = response.data;

    // handle both shapes
    let scoresArray: Score[] = [];
    if (Array.isArray(raw)) {
      scoresArray = raw as Score[];
    } else if (raw && Array.isArray((raw as ScoresResponse).scores)) {
      scoresArray = (raw as ScoresResponse).scores;
    }

    console.log("Parsed scores array:", scoresArray);
    setScores(scoresArray);
  } catch (err: any) {
    console.error("Failed to fetch scores", err);
    setError(err);
    setScores([]);
  }
};



  // add/update score
  const updateScore = (
  id: number,
  userSequenceId: number,
  sequenceId: number,
  sequenceComponentId: number,
  questionnaireId: number,
  score: number,
) => {
  setScores((prev) => {
    const existingIndex = prev.findIndex(
      (s) =>
        s.userSequenceId === userSequenceId &&
        s.sequenceComponentId === sequenceComponentId &&
        s.questionnaireId === questionnaireId
    );

    // If a matching record exists, update it
    if (existingIndex !== -1) {
      const updated = [...prev];
      updated[existingIndex] = {
        ...updated[existingIndex],
        id, // keep latest id if necessary
        score,
        doneAt: new Date().toISOString(),
      };
      return updated;
    }

    // Otherwise, create a new record
    return [
      ...prev,
      {
        id,
        userSequenceId,
        sequenceId,
        sequenceComponentId,
        questionnaireId,
        score,
        doneAt: new Date().toISOString(),
      },
    ];
  });
};

  // ✅ mark one component as done using userSequenceId
const updateSequenceComponentDone = (
  userSequenceId: number,
  sequenceComponentId: number
) => {
  setSequences((prev) =>
    prev.map((seq) =>
      seq.id === userSequenceId
        ? {
            ...seq,
            components: seq.components.map((c) =>
              c.id === sequenceComponentId ? { ...c, is_done: true, done_at: new Date().toISOString() } : c
            ),
          }
        : seq
    )
  );
};

const updateSequenceComponentProgress = (
  userSequenceId: number,
  sequenceComponentId: number,
  answeredCount: number
) => {
  setSequences(prev =>
    prev.map(seq =>
      seq.id === userSequenceId
        ? {
            ...seq,
            components: seq.components.map(c =>
              c.id === sequenceComponentId
                ? {
                    ...c,
                    answered_questions_count: answeredCount,
                  }
                : c
            ),
          }
        : seq
    )
  );
};

// ✅ mark the whole sequence as done
const updateSequenceDone = (userSequenceId: number) => {
  setSequences((prev) =>
    prev.map((seq) =>
      seq.id === userSequenceId
        ? { ...seq, status: SequenceStatusEnum.DONE }
        : seq
    )
  );
};


  return (
    <ResonanceDataContext.Provider
      value={{ sequences, scores, loading, error, fetchSequences, fetchScores, updateScore, updateSequenceComponentDone, updateSequenceComponentProgress,
    updateSequenceDone }}
    >
      {children}
    </ResonanceDataContext.Provider>
  );
};

// --- Hook ---
export const useResonanceData = () => useContext(ResonanceDataContext);
